/**
 * ClusterLocationEmbed — zoomed location preview for a single cluster.
 *
 * Uses the Google Maps Embed API (plain iframe) — Essentials SKU, unlimited
 * free. Separate key from the Maps JS API used by DemandMap so the expensive
 * interactive map surface stays small while the per-cluster detail path is
 * cost-free regardless of interaction volume.
 *
 * Query resolution:
 *   - place_id:{places_id}  — preferred, most accurate
 *   - {lat},{lng}           — fallback when no places_id
 *   - fallback card         — when neither is available
 */

import { useMemo } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';

const EMBED_HEIGHT_PX = 300;
const EMBED_ZOOM = 16;

function buildEmbedUrl({ places_id, lat, lng }, apiKey) {
  if (!apiKey) return null;

  const base = 'https://www.google.com/maps/embed/v1/place';

  if (typeof places_id === 'string' && places_id.trim() !== '') {
    const q = encodeURIComponent(`place_id:${places_id.trim()}`);
    return `${base}?key=${apiKey}&q=${q}&zoom=${EMBED_ZOOM}`;
  }

  if (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng)
  ) {
    const q = encodeURIComponent(`${lat},${lng}`);
    return `${base}?key=${apiKey}&q=${q}&zoom=${EMBED_ZOOM}`;
  }

  return null;
}

export default function ClusterLocationEmbed({ cluster }) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_EMBED_API_KEY;

  const src = useMemo(
    () => (cluster ? buildEmbedUrl(cluster, apiKey) : null),
    [cluster, apiKey]
  );

  if (!apiKey) {
    return (
      <div
        className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl p-4 text-sm flex items-center justify-center"
        style={{ height: EMBED_HEIGHT_PX }}
      >
        <div>
          <strong className="font-semibold">Map unavailable:</strong>{' '}
          <code>VITE_GOOGLE_MAPS_EMBED_API_KEY</code> is not set in{' '}
          <code>frontend/test-harness/.env</code>.
        </div>
      </div>
    );
  }

  if (!src) {
    return (
      <div
        className="bg-gray-50 border border-gray-200 text-gray-500 rounded-xl p-6 text-sm flex flex-col items-center justify-center gap-2"
        style={{ height: EMBED_HEIGHT_PX }}
      >
        <MapPinIcon className="w-8 h-8 text-gray-400" />
        <span>No location data available for this cluster.</span>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl overflow-hidden border border-gray-200 shadow-sm"
      style={{ height: EMBED_HEIGHT_PX, width: '100%' }}
    >
      <iframe
        title="Cluster location map"
        src={src}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    </div>
  );
}
